import React, { useEffect } from 'react';
import { XIcon } from 'lucide-react';
import { Container } from './Container';
import { Label } from './Label';
import { Project } from './projectData';
interface ProjectDetailProps {
  project: Project | null;
  onClose: () => void;
}
export function ProjectDetail({
  project,
  onClose
}: ProjectDetailProps) {
  useEffect(() => {
    if (!project) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose, project]);
  if (!project) {
    return null;
  }
  return <div className="fixed inset-0 z-[100] overflow-y-auto bg-brand-white" role="dialog" aria-modal="true" aria-labelledby="project-title" data-id="element-99" data-mp="3og69sq07dg1">
      <div className="sticky top-0 z-10 border-b border-brand-gray300 bg-brand-white" data-id="element-100" data-mp="334s7r6uetane">
        <Container className="flex min-h-[73px] items-center justify-between gap-6 py-[14px]" data-id="element-101" data-mp="382jyvyn8csqj">
          <span className="font-black text-[16px] tracking-[-0.01em] sm:text-[20px]" data-id="element-102" data-mp="2u2slo04jfgdl">
            THE ABIE AGENCY
          </span>
          <button type="button" onClick={onClose} className="inline-flex items-center gap-2 border-2 border-brand-black px-4 py-3 text-[12px] font-bold uppercase tracking-[0.06em] transition-colors hover:bg-brand-black hover:text-brand-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent" aria-label="Close project view" data-id="element-103" data-mp="2z489u120i307">
            <XIcon size={16} aria-hidden="true" data-id="element-104" data-mp="21fodxne28ny4" />
            Close
          </button>
        </Container>
      </div>

      <main data-id="element-105" data-mp="26d6qdnradlav">
        <Container className="py-[clamp(48px,8vw,112px)]" data-id="element-106" data-mp="26cyenz6wgm19">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)] lg:items-end lg:gap-[clamp(56px,9vw,144px)]" data-id="element-107" data-mp="byjeazi2llfh">
            <div data-id="element-108" data-mp="75hx2kue59sq">
              <Label data-id="element-109" data-mp="74yaurxpjz8w">02 · Selected work</Label>
              <h1 id="project-title" className="mt-5 text-[clamp(3.6rem,9vw,9.5rem)] font-black leading-[0.86] tracking-[-0.05em]" data-id="element-110" data-mp="3693bfzcpbx6">
                {project.name}
              </h1>
              <div className="mt-8 border-t border-brand-gray300 pt-5" data-id="element-111" data-mp="uvkkuor9vtlj">
                <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-brand-gray600" data-id="element-112" data-mp="qvto84f2ej93">
                  {project.category}
                </p>
                <p className="mt-2 text-sm font-medium" data-id="element-113" data-mp="gwkvtfzulcou">Cover: {project.coverSubject}</p>
              </div>
            </div>

            <div className="aspect-[4/5] overflow-hidden bg-brand-gray100" data-id="element-114" data-mp="36m6ens990px3">
              {project.coverVideo ? <video src={project.coverVideo} poster={project.coverImage} controls autoPlay playsInline className="h-full w-full object-cover" data-id="element-115" data-mp="31m1w7wos5l48" /> : <img src={project.coverImage} alt={`${project.name} campaign featuring ${project.coverSubject}`} className="h-full w-full object-cover" data-id="element-116" data-mp="31g19tcxsvpd3" />}
            </div>
          </div>
        </Container>

        <section className="border-y border-brand-gray300 bg-brand-gray100 py-[clamp(56px,8vw,112px)]" data-id="element-117" data-mp="3f7o63crqsxvv">
          <Container data-id="element-118" data-mp="3k8lo9pcr5rxd">
            <div className="grid gap-12 md:grid-cols-[minmax(170px,0.65fr)_minmax(0,1.35fr)] md:gap-16" data-id="element-119" data-mp="3k83hhysgrvb7">
              <Label data-id="element-120" data-mp="3p5vl3q2h15v8">Overview</Label>
              <p className="max-w-[32ch] text-[clamp(1.75rem,3.6vw,3.75rem)] font-black leading-[1.02] tracking-[-0.035em]" data-id="element-121" data-mp="1ptstfsjp156h">
                {project.overview}
              </p>
            </div>
          </Container>
        </section>

        <section className="py-[clamp(56px,8vw,112px)]" data-id="element-122" data-mp="3l8oirj1xo07o">
          <Container data-id="element-123" data-mp="3gamxqsky5qtk">
            <div className="grid gap-12 md:grid-cols-[minmax(170px,0.65fr)_minmax(0,1.35fr)] md:gap-16" data-id="element-124" data-mp="3gb691qg3dgym">
              <Label data-id="element-125" data-mp="3uao1qgwided9">What we did</Label>
              <ul className="border-t border-brand-gray300" data-id="element-126" data-mp="3u9mlly0my1oo">
                {project.deliverables.map((deliverable, index) => <li key={deliverable} className="grid grid-cols-[36px_1fr] gap-4 border-b border-brand-gray300 py-5 text-[clamp(1.2rem,1.9vw,1.65rem)] font-bold leading-[1.18] tracking-[-0.02em]" data-id="element-127" data-mp="31lqk8g04fllk" data-mpi={index}>
                    <span className="font-mono text-[11px] font-normal tracking-[0.14em] text-brand-gray600" data-id="element-128" data-mp="3bd4qd6mkvjno">
                      0{index + 1}
                    </span>
                    {deliverable}
                  </li>)}
              </ul>
            </div>
          </Container>
        </section>

        {project.gallery && project.gallery.length > 0 && <section className="border-t border-brand-gray300 py-[clamp(56px,8vw,112px)]" data-id="element-129" data-mp="tdbwodtm5rrt">
            <Container data-id="element-130" data-mp="y5vke3m0m3ac">
              <div className="mb-12" data-id="element-131" data-mp="y6euwz9c5pec">
                <Label data-id="element-132" data-mp="kdfb4l2it90z">Gallery</Label>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-133" data-mp="kf09ymvvbe4n">
                {project.gallery.map((imgSrc, index) => <div key={index} className="aspect-[4/5] overflow-hidden bg-brand-gray100" data-id="element-134" data-mp="1d2dz3ytbr5e7" data-mpi={index}>
                    <img src={imgSrc} alt={`${project.name} gallery image ${index + 1}`} className="h-full w-full object-cover" loading="lazy" data-id="element-135" data-mp="1h3fz4hv2flh8" />
                  </div>)}
              </div>
            </Container>
          </section>}
      </main>
    </div>;
}